import { AuthProvider as PackageAuthProvider, useAuth as usePackageAuth } from "@drs-ops/auth/frontend";
import type React from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { IS_DEV_BYPASS, loginRequest, msalInstance, workerTokenScopes } from "./msal-config";

/** Local dev signs in as this subject (listed in [env.dev.vars] PLATFORM_ADMINS). */
const DEV_SUBJECT = "dev:local-admin";

interface AuthContextValue {
  /** Signed in to Entra (always true under dev bypass). Access status comes from useAccess(). */
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** fetch with the Worker credential attached — the access token, or the dev subject. */
  authorizedFetch: typeof fetch;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

function AuthBridge({ children }: { children: React.ReactNode }): React.ReactElement {
  const packageAuth = usePackageAuth();

  const authorizedFetch = useCallback<typeof fetch>(
    async (input, init = {}) => {
      const headers = new Headers(init.headers);
      if (IS_DEV_BYPASS) {
        headers.set("X-Dev-Subject", DEV_SUBJECT);
      } else {
        headers.set("Authorization", `Bearer ${await packageAuth.getWorkerToken(workerTokenScopes)}`);
      }
      return fetch(input, { ...init, headers });
    },
    [packageAuth],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: IS_DEV_BYPASS || packageAuth.isAuthenticated,
      isLoading: !IS_DEV_BYPASS && !packageAuth.isReady,
      login: async () => {
        if (!IS_DEV_BYPASS) await packageAuth.login(loginRequest);
      },
      logout: async () => {
        if (IS_DEV_BYPASS) {
          window.location.reload();
          return;
        }
        await packageAuth.logout();
      },
      authorizedFetch,
    }),
    [packageAuth, authorizedFetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Role hooks live in ./useIsAdmin (import from "@/auth/useIsAdmin") so tests can mock
// useAuth without losing them.

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <PackageAuthProvider msalInstance={msalInstance} loginRequest={loginRequest} bypass={IS_DEV_BYPASS}>
      <AuthBridge>{children}</AuthBridge>
    </PackageAuthProvider>
  );
}
